import {
  useGetUsimInventoryQuery,
  usePostInventoryItemMutation,
} from "../../../../api/usim/inventory/usimInventoryApi";
import { createInventoryTableColumns } from "./InventoryTableColumns";
import { useEffect, useMemo, useState } from "react";
import useQuery from "../../../../utils/useQuery";
import { UsimInventoryItem } from "../../../../api/usim/inventory/inventoryItem";
import ItemDetailModal from "./ItemDetailModal";
import { ExportOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";

import {
  Button,
  Col,
  Dropdown,
  Flex,
  Input,
  Row,
  Table,
  Typography,
  message,
  Spin,
} from "antd";
import InventoryAddModal from "./InventoryAddModal";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
const { Title } = Typography;

export default function ManageInventoryPage() {
  const [params, setParams] = useState<{ type?: string; mobileNumber?: string; iccid?: string; limit?: number }>({ limit: 300 });
  const { data, error, isFetching } = useGetUsimInventoryQuery(params);
  const [postInventoryItem] = usePostInventoryItemMutation();
  const query = useQuery();
  const [isLoadedQuery, setIsLoadedQuery] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [detailItem, setDetailItem] = useState<UsimInventoryItem | null>(null);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isOptionsVisible, setIsOptionsVisible] = useState(false);

  // Create columns with click handler
  const [iccidSearch, setIccidSearch] = useState("");
const [mobileSearch, setMobileSearch] = useState("");
const [productTypeSearch, setProductTypeSearch] = useState("");

const handleIccidSearch = () => {
  setParams(prev => ({ ...prev, iccid: iccidSearch, limit: 2 }));
};
const handleMobileSearch = () => {
  setParams(prev => ({ ...prev, mobileNumber: mobileSearch, limit: 2 }));
};
const handleProductTypeSearch = () => {
  setParams(prev => ({ ...prev, type: productTypeSearch, limit: 2000 }));
};

const inventoryTableColumns = useMemo(
  () => createInventoryTableColumns(
    (item) => setDetailItem(item),
    {
      iccidSearch, setIccidSearch, handleIccidSearch,
      mobileSearch, setMobileSearch, handleMobileSearch,
      productTypeSearch, setProductTypeSearch, handleProductTypeSearch
    }
  ),
  [iccidSearch, mobileSearch, productTypeSearch]
);

  useEffect(() => {
    const search = query.get("search");
    if (isLoadedQuery || !search) {
      return;
    }
    setSearchQuery(search);
    setIsLoadedQuery(true);
  }, [query, isLoadedQuery]);

  const showOptions = (show: boolean) => {
    setIsOptionsVisible(show);
  };

  const processedData = useMemo(() => {
    if (!data) {
      return [];
    }

    const filtered = data.items.filter((x: UsimInventoryItem) => {
      if (searchQuery.length === 0) {
        return true;
      }
      const json = JSON.stringify(x);
      if (json.includes(searchQuery)) {
        return true;
      }
      return false;
    });

    return filtered;
  }, [data, searchQuery]);

  const filteredData =
    data?.items?.filter((x) => {
      if (searchQuery.length === 0) {
        return true;
      }
      const json = JSON.stringify(x);
      if (json.includes(searchQuery)) {
        return true;
      }
      return false;
    }) ?? [];


  const handleAdd = () => {
    setIsAddModalVisible(true);
  };
  const handleAddModalClose = () => {
    setIsAddModalVisible(false);
  };
  const handleAddModalSubmit = async (data: UsimInventoryItem) => {
    //FORMAT SHOULD BE LIKE DD-MMM-YYYY e.g. 23-JAN-2025
    data.expiryDate = dayjs(data.expiryDate).format("DD-MMM-YYYY");
    console.log("add data", data)
    await postInventoryItem(data).unwrap();
    setIsAddModalVisible(false);
  };

  const optionsMenuItems = [
    {
      key: "export",
      label: "Export",
      icon: <ExportOutlined />,
      onClick: handleExport,
    }
  ];

  // Column mapping for export (customize export headers here)
  const exportColumnMapping = {
    Id: "Id",
    iccid: "ICCID",
    mobileNumber: "Mobile Number",
    productType: "Product Type",
    productPlan: "Product Plan",
    expiryDate: "Expiry Date",
    activationDate: "Activation Date",
    orderNumber: "Order Number",
    activatedDate: "Activated Date",
    status: "Status",
    inwardTime: "Inward Time",
  };

  function handleReloadData() {
    setParams({ limit: 300 });
    setIccidSearch("");
    setMobileSearch("");
    setProductTypeSearch("");
    setSearchQuery("");
  }
  function handleExport() {
    try {
      if (filteredData.length === 0) {
        message.warning("No data to export");
        return;
      }

      // Prepare data for export
      const exportData = filteredData.map(item => ({
        [exportColumnMapping.Id]: item.Id,
        [exportColumnMapping.iccid]: item.ICCID ? item.ICCID : "-",
        [exportColumnMapping.mobileNumber]: item.mobileNumber,
        [exportColumnMapping.productType]: item.productType,
        [exportColumnMapping.productPlan]: item.planName,
        [exportColumnMapping.expiryDate]: item.expiryDate,
        [exportColumnMapping.activationDate]: item.activationDate,
        [exportColumnMapping.orderNumber]: Array.isArray(item.ordernumber) ? item.ordernumber.join(", ") : item.ordernumber,
        [exportColumnMapping.activatedDate]: item.activatedDate,
        [exportColumnMapping.inwardTime]: item.inwardTime,
        [exportColumnMapping.status]: item.status,
      }));

      // Create a new workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Apply text format to ICCID column
      const range = XLSX.utils.decode_range(worksheet["!ref"] || "");
      for (let row = range.s.r + 1; row <= range.e.r; row++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: 1 }); // Column 1 is ICCID
        if (!worksheet[cellAddress]) continue;
        worksheet[cellAddress].z = "@"; // Set format as text
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Inventory");

      // Generate Excel file and trigger download
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.href = url;
      link.download = `inventory-export-${new Date().toISOString().split("T")[0]}.xlsx`;
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Exported ${exportData.length} inventory items successfully as Excel file`);
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export data. Please try again.");
    }
  }


  return (
    <Flex gap={8} vertical>
      {/* Header with search and buttons */}
      <Row justify="space-between" align="bottom">
        <Col>
          <Title level={2}>INVENTORY MANAGEMENT</Title>
        </Col>
        <Col style={{ display: "flex", gap: 8 }}>
          {" "}
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add
          </Button>

          <Dropdown
              menu={{ items: optionsMenuItems }}
              trigger={['hover']}
              open={isOptionsVisible}
              onOpenChange={showOptions}
            >
              <Button icon={<SettingOutlined />}>
                Options
              </Button>
            </Dropdown>
          
        </Col>
      </Row>
      <InventoryAddModal
        isVisible={isAddModalVisible}
        onClose={handleAddModalClose}
        onSubmit={handleAddModalSubmit}
        isUpdate={false}
        data={undefined}
        inventoryAddOptions={[]}
      />
      <ItemDetailModal item={detailItem} onClose={() => setDetailItem(null)} />
<Row gutter={8} style={{ marginBottom: 16 }}>
  <Col flex="auto">
    <Input.Search
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      placeholder="Search all fields"
    />
  </Col>
  <Col>
    <Button onClick={handleReloadData}>Reload Data</Button>
  </Col>
</Row>
      <div style={{ overflowX: "auto" }}>
        <Table
          rowKey="Id"
          columns={inventoryTableColumns}
          dataSource={processedData}
          style={{ width: "100%", background: "#fff" }}
          scroll={{ x: 'max-content' }}
          loading={{
            indicator: (
              <div>
                <Spin />
              </div>
            ),
            spinning: isFetching,
          }}
          pagination={{
            defaultPageSize: 20,
            pageSizeOptions: [10, 20, 50, 100],
            defaultCurrent: 1,
               showTotal: (total) => `Total ${total} records`,
          }}
          size="small"
          bordered
          showHeader
          
        />
      </div>
    </Flex>
  );
}
